import { useEffect, useId, useState } from 'react';
import { api } from '../lib/api';
import { confirmDialog } from '../lib/dialogs';
import { submitJob, type SubmitJobResult } from '../lib/jobs';
import BulkJobModal from './BulkJobModal';

interface CandidateCursor { observedAt: string; candidateRowKey: string }
interface CandidateSummary {
  candidateRowKey: string;
  observedAt: string;
  completeness: 'proven' | 'capture-required';
}
interface CandidateList {
  updatePartitionKey: string;
  items: CandidateSummary[];
  nextCursor?: CandidateCursor;
  raw: { partitionKey: string; rowKey: string; etag?: string; sourceUrl: string };
}
interface CandidateInspection {
  eligible: boolean;
  reasons: string[];
  candidate?: Record<string, unknown>;
  candidateHash?: string;
  expectedRawEtag?: string;
  receipt?: Record<string, unknown>;
}

export default function WebsiteSourceUpdates({ rawPartitionKey, rawId }: {
  rawPartitionKey: string; rawId: string;
}) {
  const reasonId = useId();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<CandidateList>();
  const [cursor, setCursor] = useState<CandidateCursor>();
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<string>();
  const [inspection, setInspection] = useState<CandidateInspection>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [job, setJob] = useState<SubmitJobResult>();

  useEffect(() => {
    if (!open) return;
    let active = true;
    setBusy(true);
    setError(undefined);
    setInspection(undefined);
    setSelected(undefined);
    const params = new URLSearchParams({ rawPartitionKey, rawId });
    if (cursor) params.set('cursor', JSON.stringify(cursor));
    api.get<CandidateList>(`/api/mgmt/source-updates?${params}`).then((value) => {
      if (active) setPage(value);
    }).catch((failure: unknown) => {
      if (active) setError(failure instanceof Error ? failure.message : String(failure));
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [open, rawPartitionKey, rawId, cursor, refresh]);

  const inspect = async (candidateRowKey: string) => {
    if (!page) return;
    setBusy(true);
    setError(undefined);
    setInspection(undefined);
    setSelected(candidateRowKey);
    setReason('');
    try {
      const params = new URLSearchParams({ updatePartitionKey: page.updatePartitionKey, candidateRowKey });
      setInspection(await api.get<CandidateInspection>(`/api/mgmt/source-updates/inspect?${params}`));
    } catch (failure) { setError(failure instanceof Error ? failure.message : String(failure)); }
    finally { setBusy(false); }
  };
  const capture = async () => {
    if (!page?.raw.etag) { setError('Reload the source before capturing its current version.'); return; }
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await api.post<{ status: 'captured' | 'unchanged' }>('/api/mgmt/source-updates/capture', {
        rawPartitionKey, rawId, expectedRawEtag: page.raw.etag, relatedCandidateRowKey: selected
      });
      setNotice(result.status === 'captured'
        ? 'Current page captured for review. Published events have not changed.'
        : 'No material source change found. Published events have not changed.');
      setCursor(undefined);
      setRefresh((value) => value + 1);
    } catch (failure) { setError(failure instanceof Error ? failure.message : String(failure)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!page || !selected || !inspection?.eligible || !inspection.candidateHash
        || !inspection.expectedRawEtag || reason.trim().length < 8) {
      setError('Inspect a complete eligible capture and explain the review before applying it.');
      return;
    }
    if (!(await confirmDialog({
      title: 'Apply reviewed website capture',
      body: 'Start paid extraction from this exact capture? Existing event dates stay intact, incomplete results preserve prior events, and historical notifications are not replayed. Uncertain dates remain visible with a warning.',
      confirmLabel: 'Apply reviewed capture'
    }))) return;
    setBusy(true);
    setError(undefined);
    try {
      setJob(await submitJob({
        kind: 'website-source-update-promote', label: 'Apply reviewed website capture',
        targets: [{
          kind: 'website-source-update-promote', label: page.raw.sourceUrl,
          updatePartitionKey: page.updatePartitionKey, candidateRowKey: selected,
          candidateHash: inspection.candidateHash, expectedRawEtag: inspection.expectedRawEtag,
          reviewReason: reason.trim()
        }]
      }));
      setInspection(undefined);
      setNotice('Review queued. The background job continues if you close this page.');
    } catch (failure) { setError(failure instanceof Error ? failure.message : String(failure)); }
    finally { setBusy(false); }
  };

  return (
    <details className="mt-2" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="small">Review website updates</summary>
      {open && <div className="mt-2">
        <p className="small text-muted mb-2">
          Older held observations need a fresh complete capture before they can be applied.
          Complete capture is not date verification; uncertain dates keep a visible warning.
        </p>
        <div className="d-flex gap-2 flex-wrap mb-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy}
            onClick={() => { setCursor(undefined); setRefresh((value) => value + 1); }}>
            Refresh observations
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy || !page?.raw.etag}
            onClick={capture}>Capture current page</button>
          {page?.raw.sourceUrl && /^https?:\/\//i.test(page.raw.sourceUrl) && <a className="btn btn-sm btn-outline-secondary"
            href={page.raw.sourceUrl} target="_blank" rel="noopener noreferrer">Open source</a>}
        </div>
        {busy && <p className="small mb-2" role="status">Loading source evidence...</p>}
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {notice && <p className="small" role="status">{notice}</p>}
        {page && !page.items.length && <p className="small text-muted">No held observations for this page.</p>}
        {!!page?.items.length && <ul className="list-unstyled small mb-2">
          {page.items.map((item) => <li key={item.candidateRowKey} className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy}
              onClick={() => inspect(item.candidateRowKey)}>Inspect observation</button>
            <span>{item.observedAt ? new Date(item.observedAt).toLocaleString() : 'Observation time unknown'}</span>
            <span>{item.completeness === 'proven' ? 'Complete capture; review required' : 'Fresh capture required'}</span>
          </li>)}
        </ul>}
        {page?.nextCursor && <button type="button" className="btn btn-sm btn-outline-secondary mb-2"
          disabled={busy} onClick={() => setCursor(page.nextCursor)}>Older observations</button>}
        {inspection && <div className="mt-2">
          <p className="small fw-semibold mb-1">{inspection.eligible
            ? 'Complete capture eligible for your review' : 'This observation cannot be applied yet'}</p>
          {!!inspection.reasons.length && <ul className="small">{inspection.reasons.map((item) => <li key={item}>{item}</li>)}</ul>}
          <pre className="small border rounded p-2 overflow-auto" style={{ maxHeight: '20rem', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify({ candidate: inspection.candidate, receipt: inspection.receipt }, null, 2)}
          </pre>
          {inspection.eligible && <>
            <label htmlFor={reasonId} className="form-label small">Why is this capture safe to apply?</label>
            <textarea id={reasonId} className="form-control mb-2" rows={3} maxLength={1000}
              value={reason} onChange={(event) => setReason(event.target.value)} disabled={busy} />
            <button type="button" className="btn btn-sm btn-primary" onClick={apply}
              disabled={busy || reason.trim().length < 8}>Apply reviewed capture</button>
          </>}
        </div>}
      </div>}
      <BulkJobModal jobPk={job?.jobPk} jobId={job?.jobId} onClose={() => setJob(undefined)}
        onComplete={() => setRefresh((value) => value + 1)} />
    </details>
  );
}
